import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  WorkdayProjectsClient,
  GetWorkdayProjectsQuery,
  IWorkdayProjectsClient,
  PageContextOfWorkdayProjectsFilter,
} from "app/SharedApiClient";
import {
  CreateWorkdayProjectCommand,
  IWorkdayProjectClient,
  UpdateWorkdayProjectCommand,
  UpdateWorkdayProjectStarCommand,
  WorkdayProjectClient,
  WorkdayProjectDto,
} from "app/ApiClient";
import { LoadingState } from "types";
import { initialWorkdayProjectState, WorkdayProjectState } from "./workdayProjectState";
import { RootState } from "@store";
import { WorkdayProjectDataTableConfig } from "const/workdayProjectsVariables";

const sharedApiProjectsClient: IWorkdayProjectsClient = new WorkdayProjectsClient(process.env.REACT_APP_SHARED_API_URL);
const workdayprojectsClient: IWorkdayProjectClient = new WorkdayProjectClient(process.env.REACT_APP_API_URL);

interface GetWorkdayProjectsPayload {
  forceFetch?: boolean;
}

export const getWorkdayProjectsPage = createAsyncThunk(
  "workdayProjects/getAll",
  async (payload: GetWorkdayProjectsPayload, { getState, rejectWithValue }) => {
    try {
      const { workdayProject } = getState() as RootState;
      const { page: pageContext, filter } = workdayProject.pageContext;

      let result = workdayProject.workdayProjects;
      if (!result?.allData?.length || payload.forceFetch) {
        const allData = await workdayprojectsClient.getAllWorkdayProjects();
        result = {
          allData: allData ?? [],
          pageData: [],
          pageNumber: workdayProject.pageContext.page?.pageNumber,
          pageSize: workdayProject.pageContext.page?.pageSize,
        };
      }

      const searchString = filter?.search ?? "";
      const {
        pageNumber = WorkdayProjectDataTableConfig.DefaultPageNumber,
        pageSize = WorkdayProjectDataTableConfig.DefaultPageSize,
      } = pageContext ?? {};

      const projectFilters = (workdayProject.filters as WorkdayProjectDto) ?? {};

      let filteredData = result.allData.filter((item) =>
        searchString
          ? [item.name ?? "", item.workdayId ?? ""].some((value: string) => {
            if (value) {
              const stringValue = value.toString().toLowerCase();
              return stringValue.includes(searchString.toLowerCase());
            }
            return false;
          })
          : true,
      );

      if (Object.keys(projectFilters).length > 0) {
        filteredData = filteredData.filter((item: WorkdayProjectDto) =>
          Object.keys(projectFilters).every((key) => {
            const filterValues = projectFilters[key as keyof WorkdayProjectDto];
            if (Array.isArray(filterValues) && filterValues.length > 0) {
              const fieldValue = item[key as keyof WorkdayProjectDto];
              return filterValues.includes(fieldValue);
            }
            return true;
          }),
        );
      }

      const startIndex = (pageNumber - 1) * pageSize;
      const endIndex = startIndex + pageSize;

      result = {
        ...result,
        totalCount: filteredData.length,
        pageData: filteredData.slice(startIndex, endIndex),
      };
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const searchOriginWorkdayProjects = createAsyncThunk(
  "workdayProject/getAllProjects",
  async (payload: { search: string }) => {
    const query: GetWorkdayProjectsQuery = {
      context: {
        page: {
          pageNumber: 0,
          pageSize: 100,
        },
        filter: {
          search: payload.search,
        },
        sortList: [],
      },
    };
    const result = await sharedApiProjectsClient.getProjects(query);
    return result;
  },
);

export const createWorkdayProject = createAsyncThunk(
  "workdayProject/createWorkDayProject",
  async (command: CreateWorkdayProjectCommand, { dispatch, rejectWithValue }) => {
    try {
      const result = await workdayprojectsClient.createWorkDayProject(command);
      await dispatch(getWorkdayProjectsPage({ forceFetch: true }));
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateWorkdayProject = createAsyncThunk(
  "workdayProject/updateWorkDayProject",
  async (command: UpdateWorkdayProjectStarCommand, { rejectWithValue }) => {
    try {
      await workdayprojectsClient.updateWorkdayProjectStar(command);
      return command;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getWorkdayProjectsById = createAsyncThunk("workdayproject/getById", async (id: number, { rejectWithValue }) => {
  try {
    const project = await workdayprojectsClient.getWorkdayProjectById(id);
    return project;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updateWorkdayProjectDetails = createAsyncThunk(
  "workdayProject/updateWorkDayProjects",
  async (command: UpdateWorkdayProjectCommand, { rejectWithValue }) => {
    try {
      const result =await workdayprojectsClient.updateWorkdayProject(command);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const workdayProjectSlice = createSlice({
  name: "workdayProject",
  initialState: initialWorkdayProjectState,
  reducers: {
    resetWorkdayProject: () => ({ ...initialWorkdayProjectState}),
    setCreateWorkdayProjectDialogOpened: (state, { payload }: PayloadAction<boolean>) => {
      state.isCreateWorkdayProjectDialogOpened = payload;
      state.workdayProjects.allData=[];
    },
    setWorkdayProjectsPageContext: (state, { payload }: PayloadAction<PageContextOfWorkdayProjectsFilter>) => {
      state.pageContext = payload;
    },
    setWorkdayProjectsFilters: (state, { payload }: { payload: { [key in keyof WorkdayProjectDto]: string[] } }) => {
      if (!state.pageContext.filter) state.pageContext.filter = {};
      state.filters = { ...state.filters, ...payload };
    },
    setIsUpdateDialog: (state, { payload }: PayloadAction<boolean>) => {
      state.isUpdateDialog = payload;
    },
    setCurrentProjectId: (state, { payload }: PayloadAction<number>) => {
      state.currentProjectId = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getWorkdayProjectsPage.pending, (state) => {
      state.getListLoading = LoadingState.pending;
    });
    builder.addCase(getWorkdayProjectsPage.fulfilled, (state, action) => {
      state.getListLoading = LoadingState.succeed;
      state.workdayProjects = action.payload;
    });
    builder.addCase(getWorkdayProjectsPage.rejected, (state, action) => {
      state.getListLoading = LoadingState.failed;
      state.error = action.error.message ?? "";
    });

    builder.addCase(searchOriginWorkdayProjects.pending, (state) => {
      state.data = null;
      state.loading = LoadingState.idle;
    });
    builder.addCase(searchOriginWorkdayProjects.fulfilled, (state, { payload }) => {
      state.projectsBysearch = payload.pageData ?? [];
      state.loading = LoadingState.succeed;
    });
    builder.addCase(searchOriginWorkdayProjects.rejected, (state) => {
      state.data = null;
      state.loading = LoadingState.failed;
    });
    builder.addCase(
      updateWorkdayProject.fulfilled,
      (state, { payload }: PayloadAction<UpdateWorkdayProjectStarCommand>) => {
        const { workdayProjectId, ...updatedData } = payload; // Destructure to exclude workdayProjectId
        const projectToUpdate = state.workdayProjects.allData.find(
          (ele) => ele.id === workdayProjectId
        );
        const paginatedProjectToUpdate = state.workdayProjects.pageData.find(
          (ele) => ele.id === workdayProjectId
        );
        if (projectToUpdate) {
          Object.assign(projectToUpdate, updatedData);
        }
        if (paginatedProjectToUpdate) {
          Object.assign(paginatedProjectToUpdate, updatedData);
        }
      }
    );
    builder.addCase(updateWorkdayProject.rejected, (state, action) => {
      state.error = action.error.message ?? "";
    });

    // create
    builder.addCase(createWorkdayProject.pending, (state) => {
      state.createWorkdayProjectLoading = LoadingState.pending;
    });
    builder.addCase(createWorkdayProject.fulfilled, (state) => {
      state.createWorkdayProjectLoading = LoadingState.succeed;
    });
    builder.addCase(createWorkdayProject.rejected, (state, { error: { message } }) => {
      state.createWorkdayProjectLoading = LoadingState.failed;
      state.error = message ?? "";
    });
    // GetById
    builder.addCase(getWorkdayProjectsById.pending, (state) => {
      state.getProjectLoadingState = LoadingState.pending;
    });
    builder.addCase(getWorkdayProjectsById.fulfilled, (state, action) => {
      state.getProjectLoadingState = LoadingState.succeed;
      state.workdayProjects.allData = [action.payload];
    });
    builder.addCase(getWorkdayProjectsById.rejected, (state, { error: { message } }) => {
      state.getProjectLoadingState = LoadingState.failed;
      state.error = message ?? "";
    });
    // update
    builder.addCase(updateWorkdayProjectDetails.pending, (state) => {
      state.updateWorkdayProjectLoading = LoadingState.pending;
    });
    builder.addCase(updateWorkdayProjectDetails.fulfilled, (state) => {
      state.updateWorkdayProjectLoading = LoadingState.succeed;
      state.isCreateWorkdayProjectDialogOpened = false;
      state.isUpdateDialog = false;
    });
    builder.addCase(updateWorkdayProjectDetails.rejected, (state, { error: { message } }) => {
      state.updateWorkdayProjectLoading = LoadingState.failed;
      state.error = message ?? "";
    });
  },
});

export const {
  setCreateWorkdayProjectDialogOpened,
  setWorkdayProjectsPageContext,
  setWorkdayProjectsFilters,
  resetWorkdayProject,
  setIsUpdateDialog,
  setCurrentProjectId
} = workdayProjectSlice.actions;
export const workdaySelector = (state: RootState): WorkdayProjectState => state.workdayProject;

export default workdayProjectSlice.reducer;
